// Bottom sheet для ручной коррекции остатка по долгу — когда банк показывает другую сумму.
// Поля: новый остаток (число) и опциональная заметка.

import { useState } from 'react';
import type { Debt } from '../api/types';
import { formatWithCurrency } from '../lib/money';
import { SheetCta } from './SheetCta';
import { useMainButton } from '../lib/use-main-button';

type Props = {
  debt: Debt;
  onSubmit: (newBalanceMinor: bigint, note?: string) => void;
  onCancel: () => void;
  loading?: boolean;
};

function parseAmountToMinor(raw: string): bigint | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}

export function CorrectionSheet({
  debt,
  onSubmit,
  onCancel,
  loading = false,
}: Props): JSX.Element {
  const currentMajor = (BigInt(debt.principal_remaining_minor) / 100n).toString();
  const [amount, setAmount] = useState(currentMajor);
  const [note, setNote] = useState('');
  const minor = parseAmountToMinor(amount);
  const isValid = minor !== null;

  const oldMinor = BigInt(debt.principal_remaining_minor);
  const delta = isValid && minor !== null ? minor - oldMinor : null;

  const cta = useMainButton({
    text: 'Сохранить остаток',
    onClick: () => {
      if (isValid && minor !== null) onSubmit(minor, note.trim() || undefined);
    },
    enabled: isValid && !loading,
    progress: loading,
  });

  return (
    <div className="sheet-backdrop" onClick={onCancel} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">Коррекция · {debt.title}</div>

        <div className="sheet-stat-grid">
          <div className="sheet-stat">
            <div className="sheet-stat-label">Текущий в БД</div>
            <div className="sheet-stat-value">
              {formatWithCurrency(oldMinor, debt.currency)}
            </div>
          </div>
          {delta !== null && delta !== 0n ? (
            <div className="sheet-stat">
              <div className="sheet-stat-label">Изменение</div>
              <div
                className="sheet-stat-value"
                style={{ color: delta > 0n ? 'var(--negative)' : 'var(--positive)' }}
              >
                {delta > 0n ? '+' : '−'}
                {formatWithCurrency(delta > 0n ? delta : -delta, debt.currency)}
              </div>
            </div>
          ) : null}
        </div>

        <label className="form-field">
          <span className="form-label">Новый остаток ({debt.currency})</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={`form-input ${!isValid && amount ? 'invalid' : ''}`}
            disabled={loading}
            autoFocus
          />
          {!isValid && amount ? (
            <span className="form-hint error">Введи корректное число</span>
          ) : (
            <span className="form-hint">
              Установи сумму, как её показывает банк/кредитор. Запишется отдельным
              событием balance_correction в истории долга.
            </span>
          )}
        </label>

        <label className="form-field">
          <span className="form-label">Заметка (необязательно)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="form-input"
            placeholder="например: расхождение с приложением банка"
            disabled={loading}
            maxLength={200}
          />
        </label>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
